import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import axios from 'axios';

const defaultAvatar = `data:image/svg+xml;utf8,
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <circle cx="50" cy="50" r="50" fill="rgb(25, 118, 210)" />
  <path fill="white" d="M50 20c-11 0-20 9-20 20s9 20 20 20 20-9 20-20-9-20-20-20zm0 39c-13.2 0-40 6.6-40 20v3h80v-3c0-13.4-26.8-20-40-20z"/>
</svg>`;

const UserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(null);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setProfile(data);
      } else {
        setError(data.error || 'Failed to fetch profile');
        if (response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }
    } catch (error) {
      setError('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditedProfile(profile);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedProfile(null);
    setIsEditing(false);
    setError('');
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editedProfile)
      });

      if (response.ok) {
        setProfile(editedProfile);
        setSuccess('Profile updated successfully');
        setIsEditing(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (error) {
      setError('Failed to update profile');
    }
  };

  const handleOpenPasswordDialog = () => {
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/change-password`, {
        currentPassword,
        newPassword
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      setSuccess('Password changed successfully');
      handleClosePasswordDialog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const formatText = (text) => {
    if (!text) return '';
    return text
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box display="flex" alignItems="center" mb={4}>
          <Avatar
            src={defaultAvatar}
            sx={{ 
              width: 100, 
              height: 100, 
              mr: 3,
              bgcolor: 'primary.main'
            }}
          >
            {profile?.first_name?.[0]?.toUpperCase() || <PersonIcon />}
          </Avatar>
          <Box>
            <Typography variant="h4">
              {profile?.first_name} {profile?.last_name}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip 
                label={
                  profile?.role === 'system_admin' && !profile?.organization_id ? 'Super Admin' :
                  profile?.role === 'system_admin' && profile?.organization_id ? 'Organization Admin' :
                  profile?.role === 'unit_manager' ? 'Unit Manager' :
                  'Unit Member'
                }
                color={
                  profile?.role === 'system_admin' && !profile?.organization_id ? 'error' :
                  profile?.role === 'system_admin' && profile?.organization_id ? 'primary' :
                  profile?.role === 'unit_manager' ? 'secondary' :
                  'default'
                }
                size="small"
              />
              {profile?.organization_name && (
                <Chip 
                  label={profile.organization_name}
                  color="info"
                  variant="outlined"
                  size="small"
                />
              )}
              {profile?.is_unit_manager && (
                <Chip 
                  label="Unit Manager"
                  color="secondary"
                  size="small"
                />
              )}
              {profile?.service_unit && (
                <Chip 
                  label={formatText(profile.service_unit)}
                  color="info"
                  size="small"
                />
              )}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          {!isEditing ? (
            <Button variant="contained" onClick={handleEdit}>
              Edit Profile
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSave}>
                Save Changes
              </Button>
            </Box>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1" gutterBottom>
              {profile?.email}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Status
            </Typography>
            <Chip 
              label={profile?.status} 
              color={profile?.status === 'active' ? 'success' : 'default'}
              size="small"
            />
          </Grid>
          {profile?.organization_name && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Organization
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  {profile.organization_name}
                </Typography>
                {profile.organization_slug && (
                  <Chip 
                    label={profile.organization_slug}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Grid>
          )}
          {profile?.role === 'system_admin' && !profile?.organization_id && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Admin Type
              </Typography>
              <Chip 
                label="Super Admin (All Organizations)"
                color="error"
                size="small"
              />
            </Grid>
          )}

          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Contact Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Phone Number
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                value={editedProfile?.phone_number || ''}
                onChange={(e) => setEditedProfile({
                  ...editedProfile,
                  phone_number: e.target.value
                })}
                size="small"
                margin="dense"
              />
            ) : (
              <Typography variant="body1" gutterBottom>
                {profile?.phone_number || 'Not provided'}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Languages Spoken
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                value={editedProfile?.languages || ''}
                onChange={(e) => setEditedProfile({
                  ...editedProfile,
                  languages: e.target.value
                })}
                size="small"
                margin="dense"
              />
            ) : (
              <Typography variant="body1" gutterBottom>
                {profile?.languages || 'Not specified'}
              </Typography>
            )}
          </Grid>

          {/* Church Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Church Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Service Unit
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatText(profile?.service_unit) || 'Not assigned'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Role
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatText(profile?.role) || 'Not specified'}
            </Typography>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Address</Typography>
          </Grid>
          <Grid item xs={12}>
            {isEditing ? (
              <>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={editedProfile?.address_line1 || ''}
                  onChange={(e) => setEditedProfile({
                    ...editedProfile,
                    address_line1: e.target.value
                  })}
                  size="small"
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={editedProfile?.address_line2 || ''}
                  onChange={(e) => setEditedProfile({
                    ...editedProfile,
                    address_line2: e.target.value
                  })}
                  size="small"
                  margin="dense"
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="City"
                    value={editedProfile?.city || ''}
                    onChange={(e) => setEditedProfile({
                      ...editedProfile,
                      city: e.target.value
                    })}
                    size="small"
                    margin="dense"
                  />
                  <TextField
                    fullWidth
                    label="Postal Code"
                    value={editedProfile?.postal_code || ''}
                    onChange={(e) => setEditedProfile({
                      ...editedProfile,
                      postal_code: e.target.value
                    })}
                    size="small"
                    margin="dense"
                  />
                </Box>
              </>
            ) : (
              <Typography variant="body1">
                {profile?.address_line1}
                {profile?.address_line2 && <br />}
                {profile?.address_line2}
                {(profile?.city || profile?.postal_code) && <br />}
                {profile?.city} {profile?.postal_code}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenPasswordDialog}
          sx={{ mt: 2 }}
        >
          Change Password
        </Button>
      </Paper>

      <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Current Password"
            type="password"
            fullWidth
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Confirm New Password"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>Cancel</Button>
          <Button 
            onClick={handleChangePassword} 
            disabled={loading}
            variant="contained"
          >
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserProfile; 